import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import * as React from 'react';
import { Navigate } from 'react-router-dom';

import { useFeatureGate, useFeatureGates } from './hooks/useFeatureGates';
import { PATHS } from './paths';
import { useSidePanel } from './providers/SidePanelProvider';

/**
 * `/alerts` has no body of its own: the Notification Center is a layer of the
 * assistant pane at every width, so the route opens the full center and
 * redirects to wherever the pane is — the dashboard at `xl+`, where the side
 * column shows it, and `/assistant` below, where the pane is the route. A
 * viewer without `feat-alerts-center` has no center to open and lands on the
 * dashboard.
 *
 * This is the deep-link path only — a bookmark, a new tab, a modified click on
 * the bell. A plain bell click opens the center in place and never comes here,
 * which is why checking an alert does not replace the pane the user was
 * reading.
 *
 * `replace` is what keeps Back working: a pushed `/alerts` entry would re-enter
 * this gate on every Back press and trap the user going forward. Once the
 * center is open, the chevron and Escape are the way out of it.
 */
const AlertsRouteGate = () => {
  const enabled = useFeatureGate(FeatureGate.AlertsCenter);
  const { flags, loading } = useFeatureGates();
  const isXl = useBreakpoint('xl');
  const { showAlerts } = useSidePanel();

  // A layout effect, so the panel is already showing the center by the time
  // `<Navigate>`'s passive effect fires and unmounts this gate.
  React.useLayoutEffect(() => {
    if (enabled) {
      showAlerts();
    }
  }, [enabled, showAlerts]);

  if (loading && flags.size === 0) {
    return null;
  }
  if (!enabled) {
    return <Navigate to={PATHS.DASHBOARD} replace />;
  }
  return <Navigate to={isXl ? PATHS.DASHBOARD : PATHS.ASSISTANT} replace />;
};

export default AlertsRouteGate;
